import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  listenAllRideEvents, formatTime, phoneHref, waHref,
  unflagPassengerForPrank, setUserBlocked,
} from '../services/firebase';
import { downloadCsv } from '../utils/exportCsv';
import { STATUS_LABEL, STATUS_CLASS } from '../utils/rides';
import { useLang } from '../lang/LangContext';

export default function FlaggedRides({ rides, users, drivers, onSelectRide, isSuperAdmin = false }) {
  const { t } = useLang();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [selectedIncident, setSelectedIncident] = useState(null);

  useEffect(() => {
    const unsub = listenAllRideEvents(setEvents);
    return () => unsub?.();
  }, []);

  const rideMap = useMemo(() => {
    const m = {};
    (rides || []).forEach((r) => { m[r.id] = r; });
    return m;
  }, [rides]);

  const flaggedIncidents = useMemo(() => {
    const list = [];
    const seenEventKeys = new Set();

    // 1. Process ride events from collectionGroup
    (events || []).forEach((e) => {
      const r = rideMap[e.rideId] || {};
      let flagType = null;
      let severity = 'warn'; // 'alert', 'warn', 'info'
      let title = '';
      let detail = '';

      if (e.type === 'extra_money_asked' && e.asked) {
        flagType = 'extra_money';
        severity = 'alert';
        title = '🚨 Extra Money Demanded';
        detail = `Passenger reported captain asked for additional cash above fare (Fare: ₹${e.fare || r.fare || '—'})`;
      } else if (e.type === 'fare_check' && e.correct === false) {
        flagType = 'wrong_fare';
        severity = 'alert';
        title = '💰 Incorrect Fare';
        detail = `Passenger reported incorrect fare was charged/demanded (Fare: ₹${e.fare || r.fare || '—'})`;
      } else if (e.type === 'route_check' && e.good === false) {
        flagType = 'bad_route';
        severity = 'warn';
        title = '🗺️ Route / Detour Issue';
        detail = 'Passenger reported driver took an incorrect route or undue detour';
      } else if (e.type === 'sos_action') {
        flagType = 'sos';
        severity = 'alert';
        title = `🚨 SOS Triggered: ${e.action || 'Alert'}`;
        detail = `Emergency action triggered by passenger${e.contactCount != null ? ` (${e.contactCount} contacts notified)` : ''}`;
      } else if (e.type === 'rating_submitted' && Number(e.rating) <= 2) {
        flagType = 'low_rating';
        severity = 'warn';
        title = `😞 Low Rating: ${e.rating}★`;
        detail = `Tags: [${(e.tags || []).join(', ') || 'none'}] ${e.askedExtraMoney ? '· [⚠️ Extra money: YES]' : ''} ${e.fareCorrect === false ? '· [⚠️ Fare wrong: YES]' : ''}`;
      }

      if (flagType) {
        const key = `${e.rideId}_${e.type}_${e.id}`;
        if (!seenEventKeys.has(key)) {
          seenEventKeys.add(key);
          list.push({
            id: key,
            eventId: e.id,
            rideId: e.rideId,
            ride: r,
            flagType,
            severity,
            title,
            detail,
            at: e.at,
          });
        }
      }
    });

    // 2. Also check ride document flags (e.g. vehicleMatch == false, low rating directly on ride doc)
    (rides || []).forEach((r) => {
      if (r.vehicleMatch === false) {
        const key = `${r.id}_vehicle_mismatch`;
        if (!seenEventKeys.has(key)) {
          seenEventKeys.add(key);
          list.push({
            id: key,
            rideId: r.id,
            ride: r,
            flagType: 'plate_mismatch',
            severity: 'alert',
            title: '⚠️ Auto Plate Mismatch',
            detail: `Passenger reported arriving auto plate did not match ${r.vehicleNumber || 'registered auto'}`,
            at: r.vehicleMatchAt || r.updatedAt || r.createdAt,
          });
        }
      }
      if (r.rating && Number(r.rating) <= 2) {
        const key = `${r.id}_doc_low_rating`;
        if (!seenEventKeys.has(key)) {
          seenEventKeys.add(key);
          list.push({
            id: key,
            rideId: r.id,
            ride: r,
            flagType: 'low_rating',
            severity: 'warn',
            title: `😞 Low Rating: ${r.rating}★`,
            detail: r.ratingComment ? `Comment: "${r.ratingComment}" · Tags: [${(r.ratingTags || []).join(', ')}]` : `Tags: [${(r.ratingTags || []).join(', ')}]`,
            at: r.ratedAt || r.updatedAt,
          });
        }
      }
    });

    // 3. Process users flagged for prank / repeated cancellations
    (users || []).forEach((u) => {
      if (u.isFlaggedForPrank) {
        const key = `user_prank_${u.id}`;
        if (!seenEventKeys.has(key)) {
          seenEventKeys.add(key);
          list.push({
            id: key,
            userId: u.id,
            user: u,
            flagType: 'prank_cancellations',
            severity: 'alert',
            title: '🚫 Prank / Repeated Cancellations (3x)',
            detail: `Passenger has ${u.consecutiveCancellations || 3} consecutive cancellations after Anna assignment. Currently under 4h booking cooldown.`,
            at: u.flaggedAt || u.updatedAt,
          });
        }
      }
    });

    // Sort newest first
    return list.sort((a, b) => {
      const ta = a.at?.toMillis?.() || (a.at instanceof Date ? a.at.getTime() : 0);
      const tb = b.at?.toMillis?.() || (b.at instanceof Date ? b.at.getTime() : 0);
      return tb - ta;
    });
  }, [events, rideMap, rides, users]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return flaggedIncidents.filter((inc) => {
      if (filter !== 'all' && inc.flagType !== filter) return false;
      if (!qq) return true;
      const r = inc.ride || {};
      const u = inc.user || {};
      const hay = [
        inc.title, inc.detail, inc.rideId,
        r.customerName, r.customerPhone, r.driverName, r.driverPhone,
        r.vehicleNumber, r.pickup?.address, r.destination?.name,
        u.name, u.phone, u.phoneNumber,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(qq);
    });
  }, [flaggedIncidents, filter, q]);

  const exportRows = () => {
    const rows = [
      ['Date', 'Flag Type', 'Issue', 'Detail', 'Ride ID', 'Passenger', 'Phone', 'Captain', 'Driver Phone', 'Vehicle', 'Fare'],
      ...filtered.map((inc) => {
        const r = inc.ride || {};
        return [
          formatTime(inc.at),
          inc.flagType,
          inc.title,
          inc.detail,
          inc.rideId,
          r.customerName || '—',
          r.customerPhone || '—',
          r.driverName || '—',
          r.driverPhone || '—',
          r.vehicleNumber || '—',
          r.fare ? `₹${r.fare}` : '—',
        ];
      }),
    ];
    downloadCsv(`flagged-rides-${filter}.csv`, rows);
  };

  const alertCount = flaggedIncidents.filter((i) => i.severity === 'alert').length;

  return (
    <>
      <div className="toolbar">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">All Flags ({flaggedIncidents.length})</option>
          <option value="prank_cancellations">🚫 Prank / Fake Cancellations (3x)</option>
          <option value="extra_money">🚨 Extra Money Demanded</option>
          <option value="wrong_fare">💰 Incorrect Fare</option>
          <option value="plate_mismatch">⚠️ Auto Plate Mismatch</option>
          <option value="bad_route">🗺️ Route / Detour Issue</option>
          <option value="sos">🚨 SOS Emergency</option>
          <option value="low_rating">😞 Low Rating (≤ 2★)</option>
        </select>
        <input
          placeholder={t('Search issue, passenger, captain, phone…')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="muted">{filtered.length} incidents</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={exportRows}>{t('Export CSV')}</button>
      </div>

      <div className="card alert-card" style={{ marginBottom: 12, background: alertCount > 0 ? 'rgba(239, 68, 68, 0.08)' : undefined, borderColor: alertCount > 0 ? 'rgba(239, 68, 68, 0.3)' : undefined }}>
        <div className="card-title" style={{ color: alertCount > 0 ? '#f87171' : undefined }}>
          🚩 Passenger Safety &amp; Quality Incident Monitor
        </div>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          Real-time feed of passenger reported issues: extra money demands, incorrect fares, vehicle mismatches, bad routes, SOS alerts, and negative feedback.
        </p>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty" style={{ padding: '36px 0', textAlign: 'center' }}>
            <span style={{ fontSize: 32 }}>✨</span>
            <div style={{ fontWeight: 700, marginTop: 8 }}>{t('No flagged incidents')}</div>
            <div className="muted" style={{ fontSize: 13 }}>{t('All rides operating smoothly with zero reported complaints.')}</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>WHEN</th>
                  <th>INCIDENT / FLAG</th>
                  <th>PASSENGER</th>
                  <th>CAPTAIN</th>
                  <th>RIDE &amp; FARE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inc) => {
                  const r = inc.ride || {};
                  const isUrgent = inc.severity === 'alert';

                  return (
                    <tr key={inc.id} className={isUrgent ? 'row-urgent' : ''}>
                      <td className="mono" style={{ fontSize: 12 }}>
                        {formatTime(inc.at)}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span className={`badge ${isUrgent ? 'red' : 'orange'}`}>
                            {inc.title}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#e2e8f0', marginTop: 4, maxWidth: 320 }}>
                          {inc.detail}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{inc.user?.name || r.customerName || 'Passenger'}</div>
                        <div className="muted mono" style={{ fontSize: 11 }}>{inc.user?.phone || inc.user?.phoneNumber || r.customerPhone || '—'}</div>
                        <div className="row-actions" style={{ marginTop: 4 }}>
                          {phoneHref(inc.user?.phone || inc.user?.phoneNumber || r.customerPhone) && (
                            <a className="btn btn-ghost btn-sm" href={phoneHref(inc.user?.phone || inc.user?.phoneNumber || r.customerPhone)}>{t('Call')}</a>
                          )}
                          {waHref(inc.user?.phone || inc.user?.phoneNumber || r.customerPhone) && (
                            <a className="btn btn-ghost btn-sm" href={waHref(inc.user?.phone || inc.user?.phoneNumber || r.customerPhone, `Hi ${inc.user?.name || r.customerName || ''}, MyTownly support reaching out.`)} target="_blank" rel="noreferrer">WA</a>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.driverName || (inc.flagType === 'prank_cancellations' ? 'Multiple Annas' : 'Captain')}</div>
                        <div className="muted mono" style={{ fontSize: 11 }}>{r.vehicleNumber || (inc.flagType === 'prank_cancellations' ? 'Assigned' : '—')}</div>
                        <div className="row-actions" style={{ marginTop: 4 }}>
                          {phoneHref(r.driverPhone) && (
                            <a className="btn btn-ghost btn-sm" href={phoneHref(r.driverPhone)}>Call</a>
                          )}
                          {waHref(r.driverPhone) && (
                            <a className="btn btn-ghost btn-sm" href={waHref(r.driverPhone, `Hi ${r.driverName || ''}, MyTownly support team reaching out.`)} target="_blank" rel="noreferrer">WA</a>
                          )}
                        </div>
                      </td>
                      <td>
                        {inc.flagType === 'prank_cancellations' ? (
                          <div style={{ fontSize: 12 }}>
                            <span className="badge red" style={{ fontSize: 10 }}>4H COOLDOWN</span>
                            <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                              {inc.user?.consecutiveCancellations || 3} Strikes Recorded
                            </div>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontSize: 12 }}>
                              <strong>₹{r.fare || '—'}</strong> · <span className={`badge ${STATUS_CLASS[r.status] || 'gray'}`} style={{ fontSize: 10 }}>{STATUS_LABEL[r.status] || r.status}</span>
                            </div>
                            <div className="muted" style={{ fontSize: 11, marginTop: 2, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.pickup?.address || '—'} → {r.destination?.name || '—'}
                            </div>
                          </>
                        )}
                      </td>
                      <td>
                        {inc.flagType === 'prank_cancellations' ? (
                          isSuperAdmin ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              style={{ background: '#10b981', color: '#fff', border: 'none', fontSize: 11, padding: '5px 9px', borderRadius: 6 }}
                              onClick={async () => {
                                try {
                                  await unflagPassengerForPrank(inc.userId);
                                  toast.success('Passenger unflagged & booking access restored!');
                                } catch {
                                  toast.error('Failed to unflag passenger');
                                }
                              }}
                            >
                              ✓ Unflag
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              style={{ fontSize: 11, padding: '5px 9px', borderRadius: 6 }}
                              onClick={async () => {
                                if (window.confirm(`Permanently block ${inc.user?.name || 'this passenger'} from MyTownly?`)) {
                                  try {
                                    await setUserBlocked(inc.userId, true);
                                    toast.success('Passenger blocked');
                                  } catch {
                                    toast.error('Failed to block passenger');
                                  }
                                }
                              }}
                            >
                              🚫 Block
                            </button>
                          </div> : null
                        ) : (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setSelectedIncident(inc)}
                              title="View incident summary"
                            >
                              Incident ℹ️
                            </button>
                            {onSelectRide && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                style={{ fontSize: 11, padding: '5px 9px' }}
                                onClick={() => onSelectRide({ ...(inc.ride || {}), id: inc.rideId })}
                                title="Inspect full ride in Rides console"
                              >
                                Inspect 🔍
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedIncident && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span>Incident Details · {selectedIncident.title}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              {onSelectRide && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => onSelectRide({ ...(selectedIncident.ride || {}), id: selectedIncident.rideId })}
                >
                  🚖 Inspect Full Ride &amp; Chat →
                </button>
              )}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelectedIncident(null)}>✕ Close</button>
            </div>
          </div>
          <div className="detail-grid">
            <div><strong>FLAG TYPE</strong>{selectedIncident.title}</div>
            <div><strong>WHEN</strong>{formatTime(selectedIncident.at)}</div>
            <div><strong>DETAILS</strong>{selectedIncident.detail}</div>
            <div><strong>RIDE ID</strong><span className="mono">{selectedIncident.rideId}</span></div>
            <div><strong>PASSENGER</strong>{selectedIncident.ride?.customerName || '—'} ({selectedIncident.ride?.customerPhone || '—'})</div>
            <div><strong>CAPTAIN</strong>{selectedIncident.ride?.driverName || '—'} ({selectedIncident.ride?.driverPhone || '—'}) · {selectedIncident.ride?.vehicleNumber || '—'}</div>
            <div><strong>ROUTE</strong>{selectedIncident.ride?.pickup?.address || '—'} → {selectedIncident.ride?.destination?.name || '—'}</div>
            <div><strong>FARE</strong>₹{selectedIncident.ride?.fare || '—'}</div>
          </div>
        </div>
      )}
    </>
  );
}
